import React, { Fragment, useEffect, useState } from "react";
import { Field } from "react-final-form";
import {
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  Button,
  Row,
  Col,
  Label,
  FormGroup,
  UncontrolledTooltip,
  ListGroup,
  ListGroupItem,
  Input,
  PopoverHeader,
  PopoverBody,
  UncontrolledPopover,
  Alert,
} from "reactstrap";
import classnames from "classnames";
import column_types from "./column_types";
import { useFormState } from "react-final-form";
import { InputField, ReactSelect, ToggleField, required } from "modules/4_final_form/web";
import { getColsAttributes, conditionalCheck, fetchRequiredFormFieldData, getActiveHoldPoints, getMatchingHoldPoints, holdBypassPath, isHoldBypassed } from "./shared";
import { OnChange } from "react-final-form-listeners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationCircle, faUser } from "@fortawesome/free-solid-svg-icons";
import _ from "lodash";
import qs from "query-string";
import { connect } from "react-redux";
import { getEmployees } from "modules/employees/actions";
import { getProjects } from "modules/projects/actions";
import { getUserManagements } from "modules/user_managements/actions";
import { getAddressBookCompanies } from "modules/address_book/companies/actions";

const FormViewer = (props) => {
  const {
    custom_form,
    handleSubmit,
    formLabel,
    cancelFunction,
    outlookMode,
    parents,
    parentSelectLabel,
    edit,
    disabled,
    disabledMessage,
    touched,
    form,
    users,
  } = props;

  const latest_revision = _.last(custom_form?.revisions_attributes);
  const pages_attributes = latest_revision?.pages_attributes || [];

  let pages_to_display = _.filter(pages_attributes, (page) => {
    if (edit) {
      return page.show_on_update;
    } else {
      return page.show_on_create;
    }
  });

  useEffect(() => {
    if (!edit) {
      // Loop through the pages and set the default user id
      _.each(pages_to_display, (page, i) => {
        if (page?.default_user_id) {
          form.change(`page_data_attributes[${i}].user_id`, page?.default_user_id);
        }
      });
    }
  }, []);

  const { values, errors } = useFormState();
  const cols = getColsAttributes(custom_form);
  const matchingHoldPoints = getMatchingHoldPoints(cols, values);
  const activeHoldPoints = getActiveHoldPoints(cols, values);

  // ponytail: fields no longer fetch lists — one shot here so N inputs don't N-hit the API
  useEffect(() => {
    fetchRequiredFormFieldData(cols, props);
  }, [custom_form?.id]);
  const commentsReqdOnLastPage = _.some(values?.comments, (value, name) => {
    if (name === "__hold_bypasses") return false;
    const findCol = _.find(cols, { name: name });
    return findCol?.show_comments_at_the_end;
  });

  const attemptProperSubmit = () => {
    if (_.isNotEmpty(activeHoldPoints)) {
      const messages = _.map(activeHoldPoints, (col) => {
        const bypass = _.get(values, holdBypassPath(col.name));
        if (col.hold_point_bypassable && bypass?.bypassed && !_.trim(bypass?.comment || "")) {
          return `${col.label || col.name}: bypass comment is required`;
        }
        return col.hold_point_message || `Hold point: ${col.label || col.name} — save as draft only`;
      });
      toastr.error(messages.join("\n"));
      return;
    }
    form.change("draft", false);
    handleSubmit();
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  pages_to_display = _.filter(pages_to_display, (page, i) => {
    const previousPages = _.slice(pages_to_display, 0, i);
    const previousPagesWithComplete = _.map(previousPages, (page) => {
      const findPageData = _.find(values?.page_data_attributes, { page_guid: page?.guid });
      return { ...page, complete: findPageData?.complete };
    });

    let hidePage = _.some(previousPagesWithComplete, (page) => !page?.complete && page?.hide_next_steps);

    if (page?.conditionally_visible) {
      const last_revision = _.last(custom_form?.revisions_attributes);
      const rows = _.flatMap(last_revision?.pages_attributes, "data.rows_attributes");
      const columns = _.flatMap(rows, "cols_attributes");

      const findConditionalColumn = _.find(columns, { name: page?.conditional_field });
      const conditionalValue = _.get(values?.data, page?.conditional_field);

      if (conditionalValue) {
        hidePage = conditionalCheck(
          findConditionalColumn?.column_type,
          page?.conditional_value,
          page?.conditional_selected_value,
          page?.conditional_boolean_value,
          conditionalValue
        );
      } else {
        hidePage = true;
      }
    }

    return !hidePage;
  });

  const headers_to_display = _.cloneDeep(pages_to_display);
  // Now remove the group labels from the pages_to_display
  pages_to_display = _.filter(pages_to_display, (page) => !page.group_label);

  const search = qs.parse(props.location.search);
  const tab = search?.tab ? search?.tab : _.first(pages_to_display)?.guid;
  const [activeTab, setActiveTab] = useState(tab);

  const togglePrev = (i) => {
    const prevIndex = i - 1;

    const prevPage = pages_to_display[prevIndex];
    if (prevPage) toggle(prevPage.guid);
  };
  const toggleNext = (i) => {
    const nextIndex = i + 1;

    if (nextIndex == pages_to_display.length) {
      toggle(pages_to_display.length);
    } else {
      const nextPage = pages_to_display[nextIndex];
      if (nextPage) toggle(nextPage.guid);
    }
  };

  return (
    <div>
      {_.isNotEmpty(matchingHoldPoints) && (
        <Alert color={_.isEmpty(activeHoldPoints) ? "success" : "warning"}>
          <strong>{_.isEmpty(activeHoldPoints) ? "Hold point bypassed." : "Hold point active."}</strong>{" "}
          {_.isEmpty(activeHoldPoints)
            ? "You can submit once the rest of the form is valid."
            : "This form can only be saved as a draft until hold conditions are cleared or bypassed."}
          <ul className="mb-0 mt-2" style={{ listStyle: "none", paddingLeft: 0 }}>
            {_.map(matchingHoldPoints, (col) => {
              const bypassed = isHoldBypassed(col, values);
              const bypassOn = !!_.get(values, `${holdBypassPath(col.name)}.bypassed`);
              return (
                <li key={col.name} className="mb-2">
                  <div>
                    {bypassed ? (
                      <FontAwesomeIcon icon={faCheckCircle} className="text-success mr-1" />
                    ) : (
                      <FontAwesomeIcon icon={faExclamationCircle} className="text-warning mr-1" />
                    )}
                    {col.hold_point_message || `${col.label || col.name}`}
                  </div>
                  {col.hold_point_bypassable && (
                    <div className="mt-2 ml-3">
                      <FormGroup row className="mb-1 align-items-center">
                        <Col xs="auto">
                          <Field name={`${holdBypassPath(col.name)}.bypassed`} component={ToggleField} />
                        </Col>
                        <Label className="mb-0">Bypass hold point</Label>
                      </FormGroup>
                      {bypassOn && (
                        <Field
                          name={`${holdBypassPath(col.name)}.comment`}
                          component={InputField}
                          type="textarea"
                          label="Bypass comment"
                          placeholder="Reason for bypass (required)"
                          parse={(v) => v}
                          validate={required}
                        />
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Alert>
      )}
      <Row>
        {custom_form?.workflow_style && (
          <WorkflowCheckboxes activeTab={activeTab} toggle={toggle} headers_to_display={headers_to_display} {...props} handleSubmit={attemptProperSubmit} />
        )}
        <Col>
          {custom_form?.show_tabs && !custom_form?.workflow_style && (
            <Nav tabs>
              {_.map(pages_to_display, (page, i) => {
                return (
                  <NavItem key={i}>
                    <NavLink
                      className={classnames({ active: activeTab === page.guid })}
                      onClick={() => {
                        if (custom_form?.enable_tab_nav) {
                          toggle(page.guid);
                        }
                      }}
                      disabled={!custom_form?.enable_tab_nav}
                    >
                      {page.name}
                    </NavLink>
                  </NavItem>
                );
              })}
            </Nav>
          )}
          {_.isEmpty(pages_to_display) && <div>No Pages</div>}

          <TabContent activeTab={activeTab}>
            {_.map(pages_to_display, (page, i) => {
              const firstPage = _.first(pages_to_display);
              const lastPage = _.last(pages_to_display);

              const isFirstPage = page.guid == firstPage?.guid;
              const isLastPage = page.guid == lastPage?.guid;

              const thisPageCols = _.flatMap(page?.data?.rows_attributes, "cols_attributes");
              const thisPageInputNames = _.map(thisPageCols, "name");

              const thisPageHasErrors = _.some(thisPageInputNames, (inputName) => {
                return _.has(errors, `data.${inputName}`);
              });

              let indexOfData = _.findIndex(values?.page_data_attributes, { page_guid: page.guid });
              if (indexOfData == -1) {
                indexOfData = i;
              }
              const thisPageData = _.get(values, `page_data_attributes[${indexOfData}]`);

              let userSelected = _.find(users, { id: thisPageData?.user_id });

              return (
                <TabPane tabId={page.guid} key={i}>
                  {custom_form?.workflow_style && users && (
                    <Fragment>
                      <Row>
                        <Col md={10}></Col>
                        <Col md={2} className="text-right">
                          <div id={`add-responsible-${page.guid}`} className="link">
                            {!userSelected && (
                              <div>
                                Add Person Responsible <FontAwesomeIcon icon={faUser} />
                              </div>
                            )}
                            {userSelected && (
                              <div className="text-success">
                                {userSelected.name} <FontAwesomeIcon icon={faUser} color="green" />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <UncontrolledPopover placement="bottom" trigger="legacy" target={`add-responsible-${page.guid}`} style={{ width: "400px" }}>
                        <PopoverHeader>Person Responsible</PopoverHeader>
                        <PopoverBody>
                          <Field
                            name={`page_data_attributes[${indexOfData}].user_id`}
                            component={ReactSelect}
                            options={_.map(users, (emp) => {
                              return {
                                label: emp.name,
                                value: emp.id,
                              };
                            })}
                            isClearable
                          />

                          <OnChange name={`page_data_attributes[${indexOfData}].user_id`}>
                            {(value, previous) => {
                              if (!value) {
                                form.change(`page_data_attributes[${indexOfData}].user_id`, null);
                              } else {
                                form.change(`page_data_attributes[${indexOfData}].page_guid`, page.guid);
                              }
                            }}
                          </OnChange>
                        </PopoverBody>
                      </UncontrolledPopover>
                    </Fragment>
                  )}

                  {parents && (
                    <Fragment>
                      <Row>
                        <Label md={12} lg={2}>
                          {parentSelectLabel}
                        </Label>
                        <Col md={12} lg={4}>
                          <Field name="parent_id" component={ReactSelect} options={parents} validate={required} />
                        </Col>
                      </Row>
                      <OnChange name="parent_id">
                        {(value, previous) => {
                          // When the parent id is changed we need to update the item_id in the maintenance_item_issues_attributes to change the issue to the now selected maintenance item
                          const { custom_form, values, form } = props;
                          if (custom_form?.parent_type == "V2::Maintenance::Category") {
                            let new_maintenance_item_issues_attributes = _.clone(values?.maintenance_item_issues_attributes);
                            new_maintenance_item_issues_attributes = new_maintenance_item_issues_attributes?.map((item) => {
                              return {
                                ...item,
                                item_id: value,
                              };
                            });
                            form.change("maintenance_item_issues_attributes", new_maintenance_item_issues_attributes);
                          }
                        }}
                      </OnChange>
                      <hr />
                    </Fragment>
                  )}
                  <h5>{page?.name}</h5>
                  <hr />
                  <PageViewer
                    cols={cols}
                    page={page?.data}
                    pageObject={page}
                    thisPageData={thisPageData}
                    values={values}
                    custom_form={custom_form}
                    isActive={activeTab == i}
                  />
                  <hr />
                  {edit && (
                    <Fragment>
                      <FormGroup row>
                        <Label md={2}>Archived</Label>
                        <Col md={2}>
                          <Field name="archived" component={ToggleField} color="FF0000" />
                        </Col>
                      </FormGroup>
                      <hr />
                    </Fragment>
                  )}
                  <FooterButtons
                    togglePrev={() => togglePrev(i)}
                    toggleNext={() => toggleNext(i)}
                    isFirstPage={isFirstPage}
                    isLastPage={isLastPage}
                    handleSubmit={attemptProperSubmit}
                    thisPageHasErrors={thisPageHasErrors}
                    hasActiveHoldPoints={_.isNotEmpty(activeHoldPoints)}
                    custom_form={custom_form}
                    commentsReqdOnLastPage={commentsReqdOnLastPage}
                    formLabel={formLabel}
                    cancelFunction={cancelFunction}
                    disabled={disabled}
                    disabledMessage={disabledMessage}
                    form={form}
                    page={page}
                    indexOfData={indexOfData}
                  />
                </TabPane>
              );
            })}
            {commentsReqdOnLastPage && (
              <TabPane tabId={pages_to_display.length}>
                <CommentsPage {...props} />
                <hr />
                <FooterButtons
                  togglePrev={() => {togglePrev(pages_to_display.length)}}
                  toggleNext={attemptProperSubmit}
                  isFirstPage={false}
                  isLastPage={true}
                  thisPageHasErrors={false}
                  hasActiveHoldPoints={_.isNotEmpty(activeHoldPoints)}
                  formLabel={formLabel}
                  custom_form={custom_form}
                  handleSubmit={attemptProperSubmit}
                  cancelFunction={cancelFunction}
                  disabled={disabled}
                  disabledMessage={disabledMessage}
                  form={form}
                  indexOfData={pages_to_display.length}
                />
              </TabPane>
            )}
          </TabContent>
        </Col>
      </Row>
    </div>
  );
};

export default connect(null, { getEmployees, getProjects, getUserManagements, getAddressBookCompanies })(FormViewer);

const FooterButtons = (props) => {
  const {
    togglePrev,
    toggleNext,
    isLastPage,
    handleSubmit,
    isFirstPage,
    thisPageHasErrors,
    hasActiveHoldPoints,
    formLabel,
    custom_form,
    cancelFunction,
    commentsReqdOnLastPage,
    disabled,
    disabledMessage,
    indexOfData,
    form,
    page,
  } = props;

  let nextButtonLabel = "Next";

  if (isLastPage && !commentsReqdOnLastPage) {
    nextButtonLabel = `${formLabel} ${custom_form?.name}`;
  } else if (custom_form?.workflow_style) {
    nextButtonLabel = "Complete Page";
  }

  const handleNextButtonClick = (page) => {
    // if (custom_form?.workflow_style) {
    // If there are no errors on the page you can complete that page. Calling the form.change function to set the page as complete
    if (!thisPageHasErrors && !hasActiveHoldPoints) {
      form.change(`page_data_attributes[${indexOfData}].complete`, true);
      form.change(`page_data_attributes[${indexOfData}].page_guid`, page?.guid);
      form.change(`page_data_attributes[${indexOfData}].page_id`, page?.id);
    }
    // }

    if (commentsReqdOnLastPage) {
      toggleNext();
    } else if (isLastPage || thisPageHasErrors || hasActiveHoldPoints) {
      handleSubmit();
    } else {
      toggleNext();
    }
  };

  return (
    <Fragment>
      <Row>
        <Col>
          {!isFirstPage ? (
            <Button color="danger" fill onClick={togglePrev}>
              Previous
            </Button>
          ) : (
            <div />
          )}
        </Col>
        <Col className="text-right">
          <Button
            color="success"
            id="submit_button"
            fill
            onClick={() => {
              handleNextButtonClick(page);
            }}
            disabled={disabled && isLastPage}
          >
            {nextButtonLabel}
          </Button>
        </Col>
      </Row>
      {disabled && isLastPage && <UncontrolledTooltip target="submit_button">{disabledMessage}</UncontrolledTooltip>}
    </Fragment>
  );
};

const PageViewer = (props) => {
  const { values, cols, thisPageData, custom_form, page, pageObject, isActive } = props;

  const rows_attributes = page?.rows_attributes;
  return (
    <Fragment>
      {pageObject?.info_link && isActive && <iframe src={pageObject?.info_link} width="100%" height="1000px" />}

      {_.map(rows_attributes, (row, i) => {
        return (
          <RowViewer
            row={row}
            values={values}
            cols={cols}
            custom_form={custom_form}
            disabled={thisPageData?.complete && custom_form?.workflow_style}
            key={i}
          />
        );
      })}
    </Fragment>
  );
};

const RowViewer = (props) => {
  const {
    row: { cols_attributes },
    values,
    cols,
    disabled,
    custom_form,
  } = props;

  return (
    <Row>
      {_.map(cols_attributes, (column, i) => {
        return <ColumnViewer column={column} disabled={disabled} cols={cols} values={values} custom_form={custom_form} key={i} />;
      })}
    </Row>
  );
};

const ColumnViewer = (props) => {
  const { column, cols, values, disabled, custom_form } = props;
  const { conditionally_visible, conditional_field, conditional_value } = column;
  const SelectedFormComponent = column_types.find((c) => c.value == column.column_type)?.formComponent;

  let visible = true;
  if (conditionally_visible) {
    const findConditionalColumnType = _.find(cols, { name: conditional_field })?.column_type;
    const findConditionalColumnValue = _.get(values?.data, conditional_field);

    if (findConditionalColumnType == "toggle") {
      if (conditional_value == true) {
        visible = findConditionalColumnValue;
      } else if (conditional_value == false) {
        visible = !findConditionalColumnValue;
      }
    } else if (_.isArray(conditional_value)) {
      if (conditional_value?.[0].label == "Present") {
        visible = _.isNotEmpty(findConditionalColumnValue);
      } else {
        const conditional_values = _.map(conditional_value, (v) => v.label.toLowerCase());
        if (_.isArray(findConditionalColumnValue)) {
          const findConditionalColumnArrayValues = _.map(findConditionalColumnValue, (v) => v?.value.toLowerCase());
          visible = _.some(conditional_values, (val) => findConditionalColumnArrayValues.includes(val));
        } else {
          visible = _.includes(conditional_values, findConditionalColumnValue?.toLowerCase?.());
        }
      }
    } else {
      visible = conditional_value == findConditionalColumnValue;
    }
  }

  if (SelectedFormComponent && visible) {
    return (
      <Col md={column.desktop_width}>
        <SelectedFormComponent {...column} disabled={disabled} custom_form={custom_form} />
      </Col>
    );
  }
  return null;
};

const CommentsPage = (props) => {
  const { values, errors } = useFormState();

  const { comments } = values;
  const cols = getColsAttributes(props.custom_form);

  return (
    <Fragment>
      {_.map(comments, (value, comment_name) => {
        const findCol = _.find(cols, { name: comment_name });

        if (comments && findCol?.show_comments_at_the_end) {
          return (
            <FormGroup row>
              <Label md={2}>{`${findCol?.label} Comments`}</Label>
              <Col md={8}>
                <Field slot="list" type="textarea" component={InputField} parse={(v) => v} name={`comments.${comment_name}`} />
              </Col>
            </FormGroup>
          );
        }
      })}
    </Fragment>
  );
};

const WorkflowCheckboxes = (props) => {
  const { headers_to_display, handleSubmit, form, toggle, custom_form, activeTab } = props;
  const { values, errors, touched } = useFormState();

  return (
    <Col md={3} style={{ borderRight: "1px solid #ccc" }}>
      <ListGroup>
        {_.map(headers_to_display, (page, i) => {
          if (page.group_label) {
            return (
              <ListGroupItem key={i}>
                <div className="pl-2 pt-1 pb-1">
                  <h5>{page.name}</h5>
                </div>
              </ListGroupItem>
            );
          }

          const isActivePage = page.guid == activeTab;
          const thisPageComplete = _.get(values, `pages_attributes.${i}.complete`);
          const thisPageCols = _.flatMap(page?.data?.rows_attributes, "cols_attributes");
          const thisPageInputNames = _.map(thisPageCols, "name");

          const thisPageHasErrors = _.some(thisPageInputNames, (inputName) => {
            return _.has(errors, `data.${inputName}`);
          });

          const thisPageTouched = _.some(thisPageInputNames, (inputName) => {
            return _.get(touched, `data.${inputName}`);
          });

          let thisPageIconColor = null;
          let thisPageIcon = null;
          if (thisPageHasErrors && thisPageTouched) {
            thisPageIcon = faExclamationCircle;
            thisPageIconColor = "red";
          } else if (thisPageComplete) {
            thisPageIcon = faCheckCircle;
            thisPageIconColor = "green";
          }

          let indexOfData = _.findIndex(values?.page_data_attributes, { page_guid: page.guid });
          if (indexOfData == -1) {
            indexOfData = i;
          }

          const isComplete = _.get(values, `page_data_attributes.${indexOfData}.complete`);
          return (
            <ListGroupItem
              key={i}
              style={{ borderWidth: isActivePage && "2px", borderColor: isActivePage && "#007bff", borderStyle: isActivePage && "solid" }}
            >
              <div className="pl-2 pt-1 pb-1">
                {!page?.info_link && (
                  <Input
                    type="checkbox"
                    checked={isComplete}
                    onChange={(e) => {
                      if (thisPageHasErrors && !isComplete) {
                        e.preventDefault();
                        toastr.error("Please fix the errors before marking this page as complete");
                        form.change(`page_data_attributes[${indexOfData}].complete`, false);
                        handleSubmit();
                      } else {
                        form.change(`page_data_attributes[${indexOfData}].page_id`, page?.id);
                        form.change(`page_data_attributes[${indexOfData}].page_guid`, page.guid);
                        form.change(`page_data_attributes[${indexOfData}].complete`, !isComplete);
                      }
                    }}
                    style={{ width: "20px", height: "20px", verticalAlign: "middle" }}
                  />
                )}
                {isActivePage && (
                  <b className="pl-3" style={{ verticalAlign: "middle", textDecoration: isComplete ? "line-through" : "none" }}>
                    {page.name}
                  </b>
                )}

                {!isActivePage && (
                  <a
                    href="#"
                    onClick={() => toggle(page.guid)}
                    className="pl-3"
                    style={{ verticalAlign: "middle", textDecoration: isComplete ? "line-through" : "none" }}
                  >
                    {page.name}
                  </a>
                )}

                {thisPageIcon && <FontAwesomeIcon icon={thisPageIcon} size="2x" className="float-right" color={thisPageIconColor} />}
              </div>
            </ListGroupItem>
          );
        })}
      </ListGroup>
    </Col>
  );
};
